import { ReactNode, useCallback, useRef, useState } from 'react';

import { Tooltip, Typography, makeStyles, styled } from '@material-ui/core';

import classNames from 'classnames';

export const SidebarValueWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  '& + *': {
    marginTop: theme.spacing(3),
  },
}));

const useStyles = makeStyles(theme => ({
  outerGrid: {
    display: 'grid',
    columnGap: theme.spacing(1),
    alignItems: 'self-start',
    color: theme.palette.text.secondary,
    gridTemplateColumns: '1fr',
  },
  iconGrid: {
    gridTemplateColumns: 'max-content 1fr',
  },
  textGrid: {
    display: 'grid',
    minWidth: 0,
  },
  singleLine: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
}));

export type SidebarValueProps = {
  name: string;
  value?: ReactNode;
  icon?: ReactNode;
  singleLine?: boolean;
  popover?: boolean;
};
export const SidebarValue = ({
  name,
  value,
  icon,
  singleLine,
  popover,
}: SidebarValueProps) => {
  const classes = useStyles();
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const handleMouseEnter = useCallback(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, []);

  const valueContent = (
    <Typography
      ref={textRef}
      component="div"
      variant={popover ? 'body2' : 'body1'}
      color={popover ? 'textPrimary' : 'inherit'}
      className={classNames({ [classes.singleLine]: singleLine })}
      onMouseEnter={singleLine ? handleMouseEnter : undefined}
    >
      {value}
    </Typography>
  );

  return (
    <div
      className={classNames(classes.outerGrid, { [classes.iconGrid]: !!icon })}
    >
      {icon}
      <div className={classes.textGrid}>
        <Typography
          color={popover ? 'textSecondary' : 'textPrimary'}
          variant={popover ? 'body2' : 'body1'}
        >
          {name}
        </Typography>
        {singleLine && isOverflowing && typeof value === 'string' ? (
          <Tooltip title={value} placement="bottom-start">
            {valueContent}
          </Tooltip>
        ) : (
          valueContent
        )}
      </div>
    </div>
  );
};
